//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useCallback, useEffect, useState } from "react";

// === API ===
import {
  getFocusedField,
  getFocusedImage,
  on,
  onDirectionIndicator,
  onImage,
  removeEntry,
  removeListener,
  resetImage,
  returnFieldFocus,
  sendMessageData,
  updateFieldCoords,
} from 'api/prokey/prokey.websocket';

//#endregion

//-----------------------------------------------------------------
export function ImageCanvas(props) {
  //-----------------------------------------------------------------

  //#region [VALUES] Props

  const {
    settings
  } = props;

  const { is_full_size, is_magnifier_held } = settings || {};

  //#endregion

  //#region [VALUES] State

  // the canvas' context is stored once it's created
  const [ image_data , setImageData ] = useState( getFocusedImage() );
  const [ image      , setImage     ] = useState( null );
  const [ field      , setField     ] = useState( getFocusedField() );
  const [ block      , setBlock     ] = useState(  {}  );

  const [ ref_base        , setRefBase        ] = useState( null )
  const [ ref_magnify     , setRefMagnify     ] = useState( null )
  const [ context_base    , setContextBase    ] = useState( null );
  const [ context_magnify , setContextMagnify ] = useState( null );

  const [ image_width   , setImageWidth   ] = useState( 1 );
  const [ image_height  , setImageHeight  ] = useState( 1 );
  const [ canvas_width  , setCanvasWidth  ] = useState( 1 );
  const [ canvas_height , setCanvasHeight ] = useState( 1 );  
  const [ width_conversion  , setConversionWidth  ] = useState( 1 );
  const [ height_conversion , setConversionHeight ] = useState( 1 );

  const [ selected_item , setSelectedItem ] = useState( null );

  const [ canvas_highlights , setCanvasHighlights ] = useState( {} );

  const [ arrowDirection, setArrowDirection ] = useState(null);

  const setBaseRefNode = useCallback(node => {
    if (node !== null) {
      const context = node.getContext("2d");
      setContextBase( context );
      setRefBase( node );
    }
  }, []);

  const setMagnifyRefNode = useCallback(node => {
    if (node !== null) {
      const context = node.getContext("2d")
      setContextMagnify( context );
      setRefMagnify( node );
    }
  }, []);

  let showMagnifier = false;
  let drawHighlight = false;

  let startCoord = null;
  let endCoord = null;

  const buffer_x = 10;
  const buffer_y = 10;

  let mouse_x = 0;
  let mouse_y = 0;

  let magnifier_rect = null;
  let magnifier_mouse_follow = false;

  //#endregion

  //#region [STYLES]

  const _styles = {
    canvas_main: {
      position  : 'absolute',
      top       : is_full_size ? '' : 0,
      left      : is_full_size ? '' : 0,
      width     : is_full_size ? '' : '',
      height    : is_full_size ? '' : '',
      maxWidth  : is_full_size ? '100%' : '100%',
      maxHeight : is_full_size ? ''     : '100%',
      zIndex    : 20,
      overflow  : is_full_size ? 'none' : 'none',
      cursor    : 'crosshair',
    },
    canvas_misc: {
      position  : 'absolute',
      top       : is_full_size ? '' : 0,
      left      : is_full_size ? '' : 0,
      width     : is_full_size ? '' : '',
      height    : is_full_size ? '' : '',
      maxWidth  : is_full_size ? '100%' : '100%',
      maxHeight : is_full_size ? ''     : '100%',
      zIndex    : 20,
      overflow  : is_full_size ? 'none' : 'none',
      cursor    : 'crosshair',
    },
  }

  //#endregion

  //#region [EFFECT]
      
  useEffect( () => {
    //-----------------------------------------------------------------
    function unsubscribe() {
      //-----------------------------------------------------------------
      sendMessageData( 'TASK_VIEW_IMAGES_UNSUBSCRIBE' );
    }

    sendMessageData( 'TASK_VIEW_IMAGES_SUBSCRIBING' );

    window.addEventListener('beforeunload', unsubscribe );
    return () => {
      window.removeEventListener('beforeunload', unsubscribe );
    }
  }, [] );
  
  useEffect( () => {
    function _handleReconnect(data) {
      sendMessageData( 'TASK_VIEW_IMAGES_SUBSCRIBING', data );
    }

    on( 'connection', _handleReconnect );

    return () => {
      removeListener( 'connection', _handleReconnect );
    }
  }, [] );

  // ===

  useEffect(() => {
    if( !image ) return;

    setImageWidth ( image.naturalWidth  );
    setImageHeight( image.naturalHeight );

  }, [image, canvas_width, canvas_height]);

  // ===

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleImage(data) {
      //-----------------------------------------------------------------
      drawMagnifier();
      setImageData(data)
    }

    //-----------------------------------------------------------------
    function handleFieldFocus(data) {
      //-----------------------------------------------------------------
      setField(data);
      setArrowDirection(null);
    }

    //-----------------------------------------------------------------
    function handleBlockFocus(data) {
      //-----------------------------------------------------------------
      // if( block.id === data.id) return;
      setBlock(data);
    }

    //-----------------------------------------------------------------
    function handleImageClear(data) {
      //-----------------------------------------------------------------
      setField    (null);
      setBlock    ( {} );
      setImage    (null);
      setImageData(null);
    }
      
    on( 'IMAGE'        , handleImage      );
    on( 'FIELD-FOCUS'  , handleFieldFocus );
    on( 'BLOCK-FOCUS'  , handleBlockFocus );
    on( 'IMAGES_CLEAR' , handleImageClear );
    
    return () => {
      removeListener( 'IMAGE'        , handleImage      );
      removeListener( 'FIELD-FOCUS'  , handleFieldFocus );
      removeListener( 'BLOCK-FOCUS'  , handleBlockFocus );
      removeListener( 'IMAGES_CLEAR' , handleImageClear );
    }
  }, [] );

  useEffect( () => {
    if( !image_data ) return;

    //-----------------------------------------------------------------
    function handleImage(data) {
      //-----------------------------------------------------------------
      setImageData(data)
    }

    onImage( image_data.id, handleImage );
    
    const temp = document.createElement('img');

    temp.onload = function() {
      setImage( temp );  
    };
    
    temp.onError = function() {
      resetImage( image_data.id, image_data.s3 );
    }

    temp.src = image_data.url;
    
    return () => {
      removeListener( 'IMAGE-' + image_data.id , handleImage );
    }
  }, [image_data] )

  useEffect( () => {
    if( !image_data?.id ) return;

    //-----------------------------------------------------------------
    function handleDirection(data) {
      //-----------------------------------------------------------------
      setArrowDirection(data.direction)
    }

    onDirectionIndicator( handleDirection );

    return () => {
      removeListener( 'DIRECTION-INDICATOR' , handleDirection );
    }

  }, [image_data?.id] )

  useEffect( () => {
    if( !block.id ) return;
  
    //-----------------------------------------------------------------
    function createFieldHighlights( fields ) {
      //-----------------------------------------------------------------
      // return;
      if( !field || !image) return;
  
      const canvas_buffers = {};

      // Current Field
      const current_field_buffer = { id: field.Id, entries: new Map(), is_current: true }
  
      canvas_buffers[ field.Id ] = current_field_buffer;

      for( let index = 0; index < field.Entries.length; index++ ) {
        const entry = field.Entries[index];
        
        if( entry.PageNr != image_data.page_number ) {
          continue;
        }

        const is_selected = index === selected_item;
        let coords = getHighlightRect(entry);

        const canvas = document.createElement('canvas')
        canvas.width  = coords.width  + 4 ;
        canvas.height = coords.height + 4 ;
        const buffer = { canvas, x: coords.x, y: coords.y, is_selected };
        
        createFieldHighlight( canvas, coords, true, is_selected );
        current_field_buffer.entries.set( coords, buffer );
      }
  
      for( const block_field of fields ) {
        const is_current = block_field.Id === field.Id;
        if( is_current ) continue;
  
        const field_buffer = { id: block_field.Id, entries: new Map(), is_current }
  
        canvas_buffers[ block_field.Id ] = field_buffer;
  
        for( let index = 0; index < block_field.Entries.length; index++ ) {
          const entry = block_field.Entries[index];
          if( block_field.image_id !== image_data.id ) continue;
  
          const is_selected = index === selected_item;
  
          let coords = getHighlightRect(entry);
  
          const canvas = document.createElement('canvas')
          canvas.width  = coords.width  + 4 ;
          canvas.height = coords.height + 4 ;
          const buffer = { canvas, x: coords.x, y: coords.y, is_selected };
          
          createFieldHighlight( canvas, coords, is_current, is_selected );
          field_buffer.entries.set( coords, buffer );
        }
      }

      setCanvasHighlights( canvas_buffers );
  
      // if(drawHighlight && endCoord !== null) {
      //   context_base.fillStyle = 'rgb(255,255,0)';
      //   context_base.fillRect( startCoord.x, startCoord.y, endCoord.x-startCoord.x, endCoord.y-startCoord.y );
      // }
  
      // context_base.globalCompositeOperation = "source-over";
    }
  
    //-----------------------------------------------------------------
    function createFieldHighlight( canvas, { x, y, width, height }, is_curr_field, is_selected) {
      //-----------------------------------------------------------------
      const buffer_context = canvas.getContext('2d');
      buffer_context.drawImage( image, x, y, width, height, 0, 0, width, height );
  
      const color = is_curr_field ? 'rgb( 255, 255, 0 )' : 'rgb( 128, 255, 0 )'
  
      buffer_context.globalCompositeOperation = "darken";
  
      buffer_context.beginPath();
  
      if( is_curr_field && is_selected ) {
        buffer_context.lineWidth = 3;
        buffer_context.strokeStyle = "black";
        buffer_context.strokeRect( 2, 2, width, height);
      }
  
      buffer_context.fillStyle = color;
      buffer_context.fillRect(0, 0, width, height);
  
      buffer_context.closePath();
    }

    createFieldHighlights( block.fields );
  }, [field, block, image, selected_item] );

  useEffect( () => {
    //-----------------------------------------------------------------
    function draw() {
      //-----------------------------------------------------------------
      if( !context_base || !image ) return;
      if( image_width <= 1) return;

      context_base.clearRect(0, 0, context_base.width, context_base.height);
      context_base.drawImage(image, 0, 0, image.width, image.height);

      drawFieldHighlights();
    }

    //-----------------------------------------------------------------
    function drawFieldHighlights() {
      //-----------------------------------------------------------------
      Object.values( canvas_highlights ).forEach( _field => {
        _field.entries.forEach( (highlight) => {
          if( highlight.canvas.width > 0 && highlight.canvas.height > 0 ) {
            context_base.drawImage( highlight.canvas, highlight.x, highlight.y );
          }
        })
      })
    }
    
    draw();
  }, [image, context_base, canvas_highlights, image_width, image_height, canvas_width, canvas_height] );

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function rotatePoint(x, y, degree) {
    //-----------------------------------------------------------------
    const radian = degree * (Math.PI/180);
    const newX = Math.cos(radian) * x - Math.sin(radian) * y;
    const newY = Math.sin(radian) * x + Math.cos(radian) * y;
    return {x: Math.abs(newX), y: Math.abs(newY)};
  }


  //-----------------------------------------------------------------
  function getHighlightRect(entry) {
    //-----------------------------------------------------------------
    let skewedTopLeft     = skew( entry.Left , entry.Top    );
    let skewedBottomRight = skew( entry.Right, entry.Bottom );

    let skewedX      = parseInt( skewedTopLeft.x     *  width_conversion );
    let skewedY      = parseInt( skewedTopLeft.y     * height_conversion );
    let skewedWidth  = parseInt( skewedBottomRight.x *  width_conversion ) - skewedX;
    let skewedHeight = parseInt( skewedBottomRight.y * height_conversion ) - skewedY;
    
    return { x: skewedX, y: skewedY, width: skewedWidth, height: skewedHeight};
  }

  //-----------------------------------------------------------------
  function skew(x,y) {
    //-----------------------------------------------------------------
    let result = { x: parseInt(x), y: parseInt(y) };
    const slope = Math.tan( (image_data.info.deskewAlpha*Math.PI)/180.0 );

    if( image_data.info.deskewMode === '1' ) {
      // calculate x
      const y_start = slope > 0 ? 0 : image_data.info.deskewHeight;
      const x_offset = slope * (y - y_start);
      const x_change = (Math.round(x_offset + 0.5)); // truncate decimal
      result.x -= x_change;
    }

    const y_change = ((slope * result.x + 0.5) << 0);
    result.y += y_change;

    return result;
  }

  //-----------------------------------------------------------------
  function deskew(x,y) {
    //-----------------------------------------------------------------
    let result = { x: parseInt(x), y: parseInt(y) };
    const slope = Math.tan( (image_data.info.deskewAlpha*Math.PI)/180.0 );

    if( image_data.info.deskewMode === '1' ) {
      // calculate x
      const y_start = slope > 0 ? 0 : image_data.info.deskewHeight;
      const x_offset = slope * (y - y_start);
      const x_change = (Math.round(x_offset + 0.5)); // truncate decimal
      result.x += x_change;
    }

    const y_change = ((slope * result.x + 0.5) << 0);
    result.y -= y_change;

    return result;
  }

  //-----------------------------------------------------------------
  function highlightCoordsStart() {
    //-----------------------------------------------------------------
    if(drawHighlight) return;

    drawHighlight = true;
    const rect = ref_base.getBoundingClientRect();
    const x_conv = image_width  / context_base.canvas.clientWidth  ;
    const y_conv = image_height / context_base.canvas.clientHeight ;
    let xx = parseInt(((mouse_x - rect.left) * x_conv));
    let yy = parseInt(((mouse_y - rect.top) * y_conv));

    endCoord = null;
    startCoord = { x: xx, y: yy };
  }

  //-----------------------------------------------------------------
  function highlightCoordsEnd() {
    //-----------------------------------------------------------------
    if(!drawHighlight) return;
    const rect = ref_base.getBoundingClientRect();
    const x_conv = image_width  / context_base.canvas.clientWidth  ;
    const y_conv = image_height / context_base.canvas.clientHeight ;
    const x = parseInt(((mouse_x - rect.left) * x_conv));
    const y = parseInt(((mouse_y - rect.top) * y_conv));
    endCoord = { x, y };
  }

  //-----------------------------------------------------------------
  function saveHighlightCoords() {
    //-----------------------------------------------------------------
    if(!drawHighlight) return;
    drawHighlight = false;
    const isEndBeforeStart_X = startCoord.x > endCoord.x
    const isEndBeforeStart_Y = startCoord.y > endCoord.y
    const start_x = isEndBeforeStart_X ?   endCoord.x : startCoord.x
    const   end_x = isEndBeforeStart_X ? startCoord.x :   endCoord.x
    const start_y = isEndBeforeStart_Y ?   endCoord.y : startCoord.y
    const   end_y = isEndBeforeStart_Y ? startCoord.y :   endCoord.y
    const topLeft     = deskew( start_x / width_conversion, start_y / height_conversion );
    const bottomRight = deskew(   end_x / width_conversion,   end_y / height_conversion );
    
    if( magnifier_rect && !magnifier_mouse_follow) {
      const mag_start_x = magnifier_rect.x
      const mag_end_x   = magnifier_rect.x + magnifier_rect.width
      const mag_start_y = magnifier_rect.y
      const mag_end_y   = magnifier_rect.y + magnifier_rect.height
      const is_within_x = start_x > mag_start_x && end_x < mag_end_x;
      const is_within_y = start_y > mag_start_y && end_y < mag_end_y;
      if(  is_within_x && is_within_y ) {
        const distance_x = start_x - (magnifier_rect.x + (magnifier_rect.width*0.5));
        const distance_y = start_y - (magnifier_rect.y + (magnifier_rect.height*0.5));

        const width_converted  = (bottomRight.x - topLeft.x)*0.5;
        const height_converted = (bottomRight.y - topLeft.y)*0.5;
        
        topLeft.x -= Math.round( distance_x * 0.5 )
        topLeft.y -= Math.round( distance_y * 0.5 )
        bottomRight.x = (topLeft.x + width_converted)
        bottomRight.y = (topLeft.y + height_converted)
      }
    }

    const width  = bottomRight.x - topLeft.x;
    const height = bottomRight.y - topLeft.y;

    // prevent highlight on accidental right-click
    if( height < 15 || width < 15 ) return;

    updateFieldCoords({ field: {
      id: field?.Id,
      imageId: image_data.id,
      content: field?.Entries[0].Content,
      left: topLeft.x,
      top: topLeft.y,
      right: bottomRight.x,
      bottom: bottomRight.y,
    }});

    startCoord = null;
    endCoord = null;
  }

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onMouseDown(event) {
    //-----------------------------------------------------------------
    // Left mouse button
    if( event.button === 0 && event.ctrlKey ) {
      const rect = ref_base.getBoundingClientRect();
      const x_conv = (image_width / context_base.canvas.clientWidth) * width_conversion; ;
      const y_conv = (image_height / context_base.canvas.clientHeight) * height_conversion;
      const x = parseInt(((mouse_x - rect.left) * x_conv));
      const y = parseInt(((mouse_y - rect.top) * y_conv)); 
      setSelectedItem( null );

      // check if mouse if over field highlight
      for(let index = field.Entries.length-1; index >= 0; index--) {
        const entry = field.Entries[index];
        const entry_rect = getHighlightRect(entry);
        const is_inside_x = x > entry_rect.x-buffer_x && x < entry_rect.x + entry_rect.width+buffer_x;
        const is_inside_y = y > entry_rect.y-buffer_y && y < entry_rect.y + entry_rect.height+buffer_y;
        if(is_inside_x && is_inside_y){
          if( selected_item === index ) {
            setSelectedItem( null );
          } else {
            setSelectedItem( index );
          }
          break;
        }
      }
    }
    else if( event.button === 0 ) {
      let is_over_magnifier = false;
      if( magnifier_rect ) {
        const rect = ref_base.getBoundingClientRect();
        const x_conv = (image_width / context_base.canvas.clientWidth) * width_conversion;
        const y_conv = (image_height / context_base.canvas.clientHeight) * height_conversion;
        let mouse_x = parseInt(((event.clientX - rect.left) * x_conv));
        let mouse_y = parseInt(((event.clientY - rect.top) * y_conv));
        if(  mouse_x > magnifier_rect.x
          && mouse_x < magnifier_rect.x + (magnifier_rect.width)
          && mouse_y > magnifier_rect.y
          && mouse_y < magnifier_rect.y + (magnifier_rect.height) ) {
            is_over_magnifier = true;
        }
      }
      
      if( is_magnifier_held && showMagnifier && !is_over_magnifier ) {
        showMagnifier = false;
      } else {
        showMagnifier = true;
      }
      magnifier_mouse_follow = true;
      drawMagnifier();
    }
    // Right mouse button
    else if( event.button === 2 && event.ctrlKey ) {
      if(selected_item !== null ) {
        removeEntry( field.Id, selected_item );
      }
    }
    else if (event.button === 2){
      highlightCoordsStart();
    }
  }

  //-----------------------------------------------------------------
  function onMouseUp(event) {
    //-----------------------------------------------------------------
    // Left mouse button
    if (event.button === 0) {
      if( is_magnifier_held ) {
        magnifier_mouse_follow = false;
      } else {
        showMagnifier = false;
      }
      drawMagnifier();
    } 
    // Right mouse button
    else if (event.button === 2) {
      highlightCoordsEnd();
      saveHighlightCoords();
    }

    returnFieldFocus();
    // sendMessageData( 'FIELD-FOCUS-REQUEST' );
    window.opener?.focus();
  }

  //-----------------------------------------------------------------
  function onMouseLeave(event) {
    //-----------------------------------------------------------------
    highlightCoordsEnd();
    saveHighlightCoords();
    if( !is_magnifier_held ) {
      showMagnifier = false;
      drawMagnifier();
    }
    magnifier_mouse_follow = false
  }

  //-----------------------------------------------------------------
  function onMouseMove(event) {
    //-----------------------------------------------------------------
    mouse_x = event.clientX;
    mouse_y = event.clientY;
    // const skewed = skew(x,y);

    highlightCoordsEnd();
    drawCurrentHighlight();
    if(showMagnifier) drawMagnifier();
  }

  //-----------------------------------------------------------------
  function onKeyDown(event) {
    //-----------------------------------------------------------------
    if(showMagnifier) {
      const coord_count = field.Entries[0].Bottom < 0 ? 0 : field.Entries.length;
      const is_coord_allowed = coord_count === 0 || field.validationInfo.AllowMultipleCoords;
      if(event.ctrlKey && is_coord_allowed) highlightCoordsStart();
    }

    if(event.key === 'Delete' && selected_item !== null ) {
      removeEntry( field.Id, selected_item );
    }
  }

  //-----------------------------------------------------------------
  function onContextMenu(event) {
    //-----------------------------------------------------------------
    event.preventDefault();
    return false;
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function drawMagnifier() {
    //-----------------------------------------------------------------
    if( !context_magnify ) return;
    if(!showMagnifier || settings.magnifier_size <= 0) {
      context_magnify.clearRect(0, 0, image_width * width_conversion, image_height * height_conversion);
      return;
    }

    if( magnifier_mouse_follow ) {
      const rect = ref_base.getBoundingClientRect();
      const width  = ((400*settings.magnifier_size)/100)||1;
      const height = ((200*settings.magnifier_size)/100)||1;
      const x_conv = (image_width / context_base.canvas.clientWidth) * width_conversion;
      const y_conv = (image_height / context_base.canvas.clientHeight) * height_conversion;
      let xx = parseInt(((mouse_x - rect.left) * x_conv));
      let yy = parseInt(((mouse_y - rect.top) * y_conv));
  
      var buffer = document.createElement('canvas');
      buffer.width  = width ;
      buffer.height = height;
  
      buffer.getContext('2d').drawImage(context_base.canvas,xx-width*0.5, yy-height*0.5, width, height, 0,0, width, height );
  
      context_magnify.clearRect(0, 0, image_width * width_conversion, image_height * height_conversion);
      context_magnify.beginPath();
  
      magnifier_rect = { x: xx-width, y: yy-height, width: width*2, height: height*2 }
      context_magnify.drawImage(buffer,xx-width,yy-height,width*2, height*2);
      context_magnify.rect(xx-width,yy-height,width*2, height*2);
      context_magnify.stroke();
      context_magnify.closePath();
    } else {
      const width  = ((400*settings.magnifier_size)/100)||1;
      const height = ((200*settings.magnifier_size)/100)||1;
      var buffer = document.createElement('canvas');
      buffer.width  = width ;
      buffer.height = height;
  
      buffer.getContext('2d').drawImage(context_base.canvas,magnifier_rect.x+(width*0.5), magnifier_rect.y+(height*0.5), width, height, 0,0, width, height );
  
      context_magnify.clearRect(0, 0, image_width * width_conversion, image_height * height_conversion);
      context_magnify.beginPath();
  
      context_magnify.drawImage(buffer, magnifier_rect.x, magnifier_rect.y, width*2, height*2);
      context_magnify.rect(magnifier_rect.x, magnifier_rect.y, width*2, height*2);
      context_magnify.stroke();
      context_magnify.closePath();
    }
  }

  //-----------------------------------------------------------------
  function drawCurrentHighlight() {
    //-----------------------------------------------------------------
    if( !endCoord || !startCoord ) return;
    context_base.clearRect(0, 0, context_base.width, context_base.height);
    context_base.drawImage(image, 0, 0, image.width, image.height);

    const isEndBeforeStart_X = startCoord.x > endCoord.x
    const isEndBeforeStart_Y = startCoord.y > endCoord.y
    const start_x = isEndBeforeStart_X ?   endCoord.x : startCoord.x
    const   end_x = isEndBeforeStart_X ? startCoord.x :   endCoord.x
    const start_y = isEndBeforeStart_Y ?   endCoord.y : startCoord.y
    const   end_y = isEndBeforeStart_Y ? startCoord.y :   endCoord.y
    const topLeft     = { x : start_x / width_conversion, y: start_y / height_conversion };
    const bottomRight = { x :   end_x / width_conversion, y:   end_y / height_conversion };

    if( magnifier_rect && !magnifier_mouse_follow ) {
      const mag_start_x = magnifier_rect.x
      const mag_end_x   = magnifier_rect.x + magnifier_rect.width
      const mag_start_y = magnifier_rect.y
      const mag_end_y   = magnifier_rect.y + magnifier_rect.height
      const is_within_x = start_x > mag_start_x && end_x < mag_end_x;
      const is_within_y = start_y > mag_start_y && end_y < mag_end_y;
      if(  is_within_x && is_within_y ) {
        const distance_x = start_x - (magnifier_rect.x + (magnifier_rect.width *0.5));
        const distance_y = start_y - (magnifier_rect.y + (magnifier_rect.height*0.5));

        const  width_converted = (bottomRight.x - topLeft.x)*0.5;
        const height_converted = (bottomRight.y - topLeft.y)*0.5;
        
        topLeft.x -= Math.round( distance_x * 0.5 )
        topLeft.y -= Math.round( distance_y * 0.5 )
        bottomRight.x = (topLeft.x + width_converted)
        bottomRight.y = (topLeft.y + height_converted)
      }
    }

    const  width = bottomRight.x - topLeft.x;
    const height = bottomRight.y - topLeft.y;

    const canvas = document.createElement('canvas')
    canvas.width  = width  + 4 ;
    canvas.height = height + 4 ;
    const buffer_context = canvas.getContext('2d');
    buffer_context.drawImage( image, topLeft.x, topLeft.y, width, height, 0, 0, width, height );
    const color = 'rgb( 255, 255, 0 )';

    buffer_context.globalCompositeOperation = "darken";

    buffer_context.beginPath();

    buffer_context.fillStyle = color;
    buffer_context.fillRect(0, 0, width, height);

    buffer_context.closePath();

    context_base.drawImage( canvas, topLeft.x, topLeft.y );
  }

  //-----------------------------------------------------------------
  function DisplayDirection({direction}) {
    //---------------------------------------------------------------
    if(!direction) return null;

    const isUp = direction === 'up';
    const image_height = canvas_height || image?.naturalHeight;
    const spacing = 200;
    const arrow_number = Math.max(1, Math.round(image_height / spacing));
    const visual = [];
    for  ( let i = 0; i < arrow_number; i++ ) {
      visual.push(i);
    }

    return (
      <div className={`arrow-visual ${isUp ? 'up' : 'down'}`}>
        {visual.map((x) => (
          <div key={x} className={isUp ? 'arrow-item' : 'arrow-item-reverse' } style={{ top: `${(image_height / arrow_number ) * x}px`, animationDelay: `${(isUp ? (arrow_number-x) : x )* 150}ms` }} >
            {isUp ? '⬆' : '⬇'}
          </div>
        ))}
      </div>
    );
  }

  //-----------------------------------------------------------------
  function display() {
    //---------------------------------------------------------------
    
    if(!image_data || !image) return (null);
    else {
      return (
        <>
          <canvas
            name='current_image_canvas'
            style={_styles.canvas_main}
            ref={setBaseRefNode}
            height={image.naturalHeight}
            width={image.naturalWidth}
            tabIndex={999}
            onMouseUp={onMouseUp}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
            onMouseMove={onMouseMove}
            onKeyDown={onKeyDown}
            onContextMenu={onContextMenu}
          />
          <canvas
            name='current_image_canvas'
            style={_styles.canvas_misc}
            ref={setMagnifyRefNode}
            height={image.naturalHeight}
            width={image.naturalWidth}
            tabIndex={999}
            onMouseUp={onMouseUp}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
            onMouseMove={onMouseMove}
            onKeyDown={onKeyDown}
            onContextMenu={onContextMenu}
          />
          <DisplayDirection direction={arrowDirection} />
        </>
      );
    }
  }

  //#endregion

  return display();
};
